import { Button, Flex, IconButton, Input, Select, Td, Tr } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MoneyField from "components/elements/MoneyInput";
import { LoanContext } from "providers/loan";
import { useContext, useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { getErrorMessage } from "utils/errorHandler";

const AddDrawItem = ({ section }: { section: number }) => {
  const { loan, refetch: loadLoan } = useContext(LoanContext);

  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showError } = useContext(AlertsContext);
  const [readLoan, setReadLoan] = useState(false);
  const [drawPermission, setDrawPermission] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const readOnlyLoan = () => {
    if (isPermissionEnabled(user) && hasPermission(user, permissionConstants.readOnlyLoans)) {
      setReadLoan(true);
    }
    if (isPermissionEnabled(user) && hasPermission(user, permissionConstants.approveDraw150k)) {
      setDrawPermission(true);
      setReadLoan(false);
    }
    if (hasPermission(user, permissionConstants.viewPayments)) {
      setReadLoan(false);
    }
  };

  const handleMoneyInput = (amount: string | number) => {
    const numericAmount = Number(amount);
    if (drawPermission) {
      if (numericAmount <= 150000) {
        setDisableSubmit(false);
        setNewItem({
          ...newItem,
          amount: amount.toString(),
        });
      } else {
        setDisableSubmit(true);
      }
    } else {
      setNewItem({
        ...newItem,
        amount: amount.toString(),
      });
    }
  };

  useEffect(() => {
    readOnlyLoan();
  }, []);

  const [newItem, setNewItem] = useState<{
    title?: string;
    due_date?: string;
    amount?: string;
    status?: string;
  }>({});

  const onDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = event.target.value;
    setNewItem({
      ...newItem,
      due_date: selectedDate,
    });
  };

  const { user } = useTenant();

  const addNewItem = async () => {
    try {
      setLoading(true);
      await apiRequest("/supabase/draw_requests/insert", {
        loan_id: loan!.id,
        section,
        ...newItem,
      });
      await loadLoan();
      setAdding(false);
      setNewItem({});
    } catch (err) {
      showError({
        title: "Request Failed",
        description: getErrorMessage(err),
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return adding ? (
    <>
      <Tr>
        <Td>
          <Input
            value={newItem.title}
            onChange={ev =>
              setNewItem({
                ...newItem,
                title: ev.target.value,
              })
            }
            placeholder="Untitled Item"
            variant="primary"
          />
        </Td>
        <Td>
          <Input type="date" value={newItem.due_date} onChange={onDateChange} variant="primary" />
        </Td>
        <Td>
          <Select
            variant="primary"
            value={newItem.status}
            onChange={ev =>
              setNewItem({
                ...newItem,
                status: ev.target.value,
              })
            }
          >
            <option value="unpaid">Unpaid</option>
            <option value="pending">Pending</option>
            <option value="declined">Declined</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
          </Select>
        </Td>
        <Td>
          <MoneyField
            value={newItem.amount || ""}
            onValueChange={amount => handleMoneyInput(amount ?? "")}
          />
          {disableSubmit && (
            <p style={{ color: "red", fontSize: "12px" }}>You cannot draw more than 150k</p>
          )}
        </Td>
        <Td>
          <Flex justifyContent="flex-end">
            <IconButton
              variant="ghost"
              aria-label="draw-request"
              borderColor="primary"
              color="primary"
              onClick={addNewItem}
              isLoading={loading}
              isDisabled={disableSubmit}
              icon={
                <>
                  <FontAwesomeIcon icon={["fas", "check"]} />
                </>
              }
            />
            <IconButton
              variant="ghost"
              aria-label="draw-request"
              borderColor="primary"
              onClick={() => setAdding(false)}
              isDisabled={disableSubmit}
              icon={
                <>
                  <FontAwesomeIcon icon={["fas", "times"]} />
                </>
              }
            />
          </Flex>
        </Td>
      </Tr>
    </>
  ) : (
    <Tr>
      <Td colSpan={5}>
        <Button
          variant="ghost"
          p="0px"
          color="gray.200"
          fontSize="14px"
          fontWeight="400"
          onClick={() => setAdding(true)}
          isDisabled={readLoan}
          isLoading={loading}
          leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
        >
          Add new item...
        </Button>
      </Td>
    </Tr>
  );
};

export default AddDrawItem;
